import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, TextArea, Select, Field, Toggle } from '@kontentino/ui';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';

import { CaptionFormValues } from '..';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from 'react-query';
import { BrandHubApi } from 'app/modules/brandHub/api/brandHub';
import { queryKey } from 'constants/queryKey';

interface Props {
  children?: React.ReactNode;
  onSubmit: (values: Omit<CaptionFormValues, 'pageType'>) => void;
  hasGeneratedData?: boolean;
}

const formSchema = z.object({
  postText: z.string().min(1, 'Please describe your post'),
  postType: z.string().min(1, 'Post type is required'),
  writingStyle: z.string().min(1, 'Writing style is required'),
  captionLength: z.string().min(1, 'Caption length is required'),
  variationsCount: z.number().min(1, 'Variations count is required'),
  language: z.string().min(1, 'Language is required'),
  useEmojis: z.boolean(),
  useHashtags: z.boolean(),
  expanded: z.boolean().optional(),
});

const INITIAL_VALUES: Omit<CaptionFormValues, 'pageType'> = {
  postText: '',
  postType: '',
  writingStyle: '',
  captionLength: '',
  variationsCount: 0,
  language: 'en',
  useEmojis: true,
  useHashtags: false,
  expanded: false,
};

const AiCaptionGeneratorForm: FC<Props> = ({
  children,
  onSubmit,
  hasGeneratedData,
}) => {
  const { t } = useTranslation();
  const { control, handleSubmit, reset } = useForm<
    Omit<CaptionFormValues, 'pageType'>
  >({
    defaultValues: INITIAL_VALUES,
    resolver: zodResolver(formSchema),
  });

  const creativesOptionsQuery = useQuery(
    queryKey.brandHubCreativesOptions(),
    BrandHubApi.publicCreativesOptions,
    {
      onSuccess: (data) => {
        reset({
          ...INITIAL_VALUES,
          postType: data.postType[0]?.value || '',
          writingStyle: data.toneOfVoice[0]?.value || '',
          captionLength: data.creativeLength[0]?.value || '',
          variationsCount: data.counts[0]?.value || 1,
          language: data.language[0]?.value || 'en',
        });
      },
    },
  );

  const isOptionsLoading = creativesOptionsQuery.isLoading;

  const creativeOptions = {
    postTypes: creativesOptionsQuery.data?.postType || [],
    toneOfVoice: creativesOptionsQuery.data?.toneOfVoice || [],
    creativeLength:
      creativesOptionsQuery.data?.creativeLength?.map((item) => ({
        ...item,
        label: t(`creativesLength.${item.value}`),
      })) || [],
    counts:
      creativesOptionsQuery.data?.counts?.map((item) => ({
        ...item,
        label: t('countOfCreatives', { count: item.value }),
      })) || [],
    languages: creativesOptionsQuery.data?.language || [],
  };

  const onFormSubmit = (values: Omit<CaptionFormValues, 'pageType'>) => {
    onSubmit(values);
  };

  const postTypeOptions = creativeOptions.postTypes;
  const writingStyleOptions = creativeOptions.toneOfVoice;
  const captionLengthOptions = creativeOptions.creativeLength;
  const variationCountOptions = creativeOptions.counts;

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <div className="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-grid-cols-3">
        <div className="tw-flex tw-flex-col tw-space-y-5 tw-rounded-lg tw-border tw-border-grayscale-20 tw-bg-grayscale-10/30 tw-p-5">
          <Field.Group>
            <Field.Label required>Post type</Field.Label>
            <Controller
              name="postType"
              control={control}
              render={({ field }) => (
                <div data-name="ai-caption-generator_post-type_select-wrapper">
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 1010,
                      }),
                    }}
                    value={
                      postTypeOptions.find(
                        (option) => option.value === field.value,
                      ) || null
                    }
                    isDisabled={isOptionsLoading}
                    onChange={(option) => field.onChange(option?.value || '')}
                    options={postTypeOptions}
                  />
                </div>
              )}
            />
          </Field.Group>

          <Field.Group>
            <Field.Label required>{t('toneOfVoice')}</Field.Label>
            <Controller
              name="writingStyle"
              control={control}
              render={({ field }) => (
                <div data-name="ai-caption-generator_tone-of-voice_select-wrapper">
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 1010,
                      }),
                    }}
                    value={
                      writingStyleOptions.find(
                        (option) => option.value === field.value,
                      ) || null
                    }
                    isDisabled={isOptionsLoading}
                    onChange={(option) => field.onChange(option?.value || '')}
                    options={writingStyleOptions}
                  />
                </div>
              )}
            />
          </Field.Group>

          <Field.Group>
            <Field.Label required>{t('lengthOfCreatives')}</Field.Label>
            <Controller
              name="captionLength"
              control={control}
              render={({ field }) => (
                <div data-name="ai-caption-generator_length_select-wrapper">
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 1010,
                      }),
                    }}
                    value={
                      captionLengthOptions.find(
                        (option) => option.value === field.value,
                      ) || null
                    }
                    isDisabled={isOptionsLoading}
                    onChange={(option) => field.onChange(option?.value || '')}
                    options={captionLengthOptions}
                  />
                </div>
              )}
            />
          </Field.Group>

          <Field.Group>
            <Field.Label required>{t('countOfCreatives')}</Field.Label>
            <Controller
              name="variationsCount"
              control={control}
              render={({ field }) => (
                <div data-name="ai-caption-generator_count_select-wrapper">
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 1010,
                      }),
                    }}
                    value={
                      variationCountOptions.find(
                        (option) => option.value === field.value,
                      ) || null
                    }
                    isDisabled={isOptionsLoading}
                    onChange={(option) => field.onChange(option?.value || 1)}
                    options={variationCountOptions}
                  />
                </div>
              )}
            />
          </Field.Group>

          <Field.Group>
            <Field.Label required>{t('outputLanguage')}</Field.Label>
            <Controller
              name="language"
              control={control}
              render={({ field }) => (
                <div data-name="ai-caption-generator_language_select-wrapper">
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (styles) => ({
                        ...styles,
                        zIndex: 1010,
                      }),
                    }}
                    value={
                      creativeOptions.languages.find(
                        (option) => option.value === field.value,
                      ) || null
                    }
                    isDisabled={isOptionsLoading}
                    onChange={(option) => field.onChange(option?.value || '')}
                    options={creativeOptions.languages}
                  />
                </div>
              )}
            />
          </Field.Group>

          <div className="tw-flex tw-flex-col tw-space-y-2">
            <div className="tw-flex tw-items-center tw-justify-between">
              <span className="tw-text-sm tw-font-medium tw-text-grayscale-180">
                Use emojis
              </span>
              <Controller
                name="useEmojis"
                control={control}
                render={({ field }) => (
                  <Toggle
                    checked={field.value}
                    onChange={() => field.onChange(!field.value)}
                    size="small"
                    data-name="ai-caption-generator_emojis_toggle"
                  />
                )}
              />
            </div>
            <div className="tw-flex tw-items-center tw-justify-between">
              <span className="tw-text-sm tw-font-medium tw-text-grayscale-180">
                Use hashtags
              </span>
              <Controller
                name="useHashtags"
                control={control}
                render={({ field }) => (
                  <Toggle
                    checked={field.value}
                    onChange={() => field.onChange(!field.value)}
                    size="small"
                    data-name="ai-caption-generator_hashtags_toggle"
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className="md:tw-col-span-2">
          <Field.Group>
            <Field.Label required>{t("What's your post about?")}</Field.Label>
            <div className="tw-rounded-lg tw-border tw-border-purple-100/20 tw-bg-gradient-to-br tw-from-primary-50/10 tw-via-purple-50/10 tw-to-purple-50/10 tw-p-1">
              <Controller
                name="postText"
                control={control}
                render={({ field, fieldState }) => (
                  <TextArea
                    {...field}
                    placeholder={t('Describe your post content here...')}
                    className="tw-min-h-[140px] tw-bg-transparent focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary-50/30"
                    error={!!fieldState.error}
                    data-name="ai-caption-generator_post-text_textarea"
                  />
                )}
              />
            </div>
            <Controller
              name="postText"
              control={control}
              render={({ fieldState }) => (
                <>
                  {fieldState.error && (
                    <Field.Error>Please describe your post</Field.Error>
                  )}
                </>
              )}
            />
          </Field.Group>
          <div className="tw-mt-4 tw-flex tw-justify-end tw-gap-3">
            <Button
              type="submit"
              variant="primary"
              className="tw-bg-gradient-to-r tw-from-purple-100 tw-from-0% tw-to-primary-100 tw-to-100% hover:tw-from-purple-120 hover:tw-to-primary-120"
              iconBefore={<Icon icon={faSparkles} />}
              data-name="ai-caption-generator-generate"
              disabled={isOptionsLoading}
            >
              {hasGeneratedData ? 'Regenerate caption' : 'Generate caption'}
            </Button>
          </div>
          {children}
        </div>
      </div>
    </form>
  );
};

export default AiCaptionGeneratorForm;
